import { cn } from '../lib/utils';

/**
 * LiveBadge - Reusable red LIVE indicator component
 * Used across the platform for consistent live status display
 * 
 * @param {string} variant - 'default' | 'compact' | 'large' | 'dot-only'
 * @param {boolean} pulse - Whether to show pulsing animation
 * @param {string} className - Additional CSS classes
 */
const LiveBadge = ({ 
  variant = 'default', 
  pulse = true, 
  className = '',
  showText = true 
}) => {
  const variants = {
    // Standard badge - used on cards, match center
    default: 'px-3 py-1.5 text-xs',
    // Compact - used in ticker, smaller spaces
    compact: 'px-2 py-1 text-[10px]',
    // Large - used on hero sections
    large: 'px-4 py-2 text-sm',
    // Dot only - minimal indicator
    'dot-only': 'p-0'
  };

  const dotSizes = {
    default: 'w-2 h-2',
    compact: 'w-1.5 h-1.5',
    large: 'w-2.5 h-2.5',
    'dot-only': 'w-3 h-3'
  };

  if (variant === 'dot-only') {
    return (
      <span
        className={cn(
          'inline-block rounded-full bg-red-500',
          pulse && 'animate-pulse',
          dotSizes[variant],
          className
        )}
        role="status"
        aria-label="Live"
        data-testid="live-badge-dot"
      />
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 bg-red-600 text-white font-bold uppercase tracking-wide rounded',
        variants[variant],
        className
      )}
      role="status"
      aria-label="Live broadcast"
      data-testid="live-badge"
    >
      <span 
        className={cn(
          'rounded-full bg-white',
          pulse && 'animate-pulse',
          dotSizes[variant]
        )}
        aria-hidden="true"
      />
      {showText && <span>Live</span>}
    </span>
  );
};

export default LiveBadge;
